import momenttz from 'moment-timezone'
import { logWarn } from 'utils/logUtils'

// Greyfinch bookings arrive as already-local strings (date "YYYY-MM-DD", time "HH:mm:ss") — no timezone
// conversion, just display formatting to match the reference ("Thursday, July 20, 2023", "03:00 pm").
// Parse strictly and guard with isValid so a truthy-but-unparseable vendor value renders '' rather than
// moment's literal "Invalid date" inside the clinical panel.

// The vendor owns this format (mapBooking passes localStart* straight through with no validation, and the
// original design doc specified full ISO). If it ever drifts, strict parsing blanks the cell silently — so
// log the raw value on a truthy-but-invalid input to surface the drift as an alert instead of a blank row.
const warnUnparseable = (kind, raw) => logWarn('Greyfinch panel - unparseable booking value', { kind, raw })

export const formatApptDate = date => {
  if (!date) return ''
  const m = momenttz(date, 'YYYY-MM-DD', true)
  if (!m.isValid()) {
    warnUnparseable('date', date)
    return ''
  }
  return m.format('dddd, MMMM DD, YYYY')
}

export const formatApptTimeRange = (time, endTime) => {
  const start = time ? momenttz(time, 'HH:mm:ss', true) : null
  if (time && (!start || !start.isValid())) {
    warnUnparseable('time', time)
  }
  if (!start || !start.isValid()) return ''
  const startStr = start.format('hh:mm a')
  const end = endTime ? momenttz(endTime, 'HH:mm:ss', true) : null
  if (endTime && (!end || !end.isValid())) {
    warnUnparseable('endTime', endTime)
  }
  if (!end || !end.isValid()) return startStr
  return `${startStr} - ${end.format('hh:mm a')}`
}

// Type-guard the vendor value: `note` is passed through unvalidated by the backend (mapTxPlan returns
// `treatment.notes ?? null`), so a shape change to `{ text }` or `[...]` would reach `.split` and throw —
// and with no error boundary in the app that white-screens the whole patient page. Non-string → no lines.
export const notesToLines = notes =>
  (typeof notes === 'string' ? notes : '')
    .split('\n')
    .map(line => line.trim())
    .filter(Boolean)

// Coerce an appointments collection to an array for rendering. A present-but-non-array value is contract
// drift (the backend's mapPatientSummary always emits arrays), so surface it via logWarn instead of
// silently rendering an affirmative "no appointments" a doctor could act on. Absent/null → quietly [].
export const coerceApptList = (list, kind) => {
  if (Array.isArray(list)) return list
  if (list != null) {
    logWarn('Greyfinch panel - appointments field is not an array', { kind, received: typeof list })
  }
  return []
}
